// gamerenderer.ts ↔ the RENDER stack extracted out of GameSession (the display
// partition + form construction of Game::StartSim, game/game.cpp:1276-1392,
// 2030-2105, plus Game::SizeAndPositionEverything game.cpp:2082-2119 and the
// Display::SetSize/Game::OnDisplayResize resize arm game.cpp:1258-1390).
//
// A GameRenderer owns the draw stack — the back dib + its sim/input partitions,
// the form managers (gpmfrmm/gpfrmmSim/gpfrmmInput), the in-game HUD forms
// (SimUIForm/InputUIForm/MiniMap), the tile map + fog renderer, the overlay
// sprite manager, the fonts/strings/gob-draw context — but NO session lifecycle
// (no clock, no OnTimer, no transport, no MP overlays). It renders a sim that
// someone else drives: GameSession composes one with its real collaborators
// (the input slice, the timer/event managers, sound, options); the replay
// viewer composes one with render-only stubs.
//
// This is a PURE RELOCATION out of gamesession.ts — the construction order and
// every draw is byte-identical (pixel-vectors oracle proven across 832/824/2432).
// The forward-referenced collaborators GameSession builds AFTER the renderer
// (buildui, shellEnv, the sim-side host seams) are reached through the lazy
// `deps.*` getters below — the closures resolve at call-time, after GameSession
// re-exposes the renderer's fields as `this.*` and finishes its ctor.

import { CpuDibBitmap } from '../gfx/dibbitmap.ts';
import type { CoordMap } from '../gfx/coords.ts';
import type { TexAtlasMgr } from '../gfx/texatlasmgr.ts';
import type { PackFileReader } from '../io/packfile.ts';
import { LoadTileMap } from '../gfx/tilemap.ts';
import type { TileMap } from '../gfx/tilemap.ts';
import { ClientFogDraw } from '../gfx/fogdraw.ts';
import { LoadFont } from '../gfx/font.ts';
import type { Font } from '../gfx/font.ts';
import { StringTable } from '../io/stringtable.ts';
import { GobDrawContext } from '../render/gobdraw.ts';
import type { IniReader, IniSection } from '../../../sim/src/data/ini.ts';
import type { EventMgr } from './event.ts';
import { kfRedrawDirty } from './event.ts';
import type { TimerMgr } from './timer.ts';
import { Rect } from './rect.ts';
import { FormMgr, MultiFormMgr, makeFormMgrHost, type ModalForm } from '../render/paintengine.ts';
import type { SimUIForm as SimUIFormInput } from './fingerhandler.ts';
import { SimUIForm, type SimUIHudDeps } from './simuiform.ts';
import type { SimUIModel } from '../dom/ingame/simui-view.ts';
import { InputUIForm, type InputUIDeps } from './inputui.ts';
import type { SoundMgr, OptionsState } from './shellform.ts';
import type { AnimData } from '../../../sim/src/anim/animation.ts';
import { GetRankTitle } from '../io/stringtable.ts';
import { kfHcapEasy, kfHcapNormal, kfHcapHard } from '../../../sim/src/player/side.ts';
import { SpriteManager, AnimSprite } from '../render/sprites.ts';

/** The seams a GameRenderer reaches outside its own draw stack. GameSession
 *  supplies the real collaborators; the replay viewer supplies render-only
 *  stubs (a SimUIFormInput over a null/no-op input host, fresh timer/event
 *  managers that are never ticked, a MemorySoundMgr, default options, and
 *  no-op/null lifecycle seams). The closures that are reached only at call-time
 *  (the input/lifecycle reaches) are LAZY — they resolve forward-referenced
 *  collaborators (buildui, shellEnv, buildDeps) after the host finishes its
 *  construction. */
export interface GameRendererDeps {
  /** Opt IN to damage-driven scoped invalidation (see frameStartInvalidate).
   *  Scoped mode is only correct when the embedder has wired the playfield damage
   *  seams on the sim host — fog/galaxite tile invalidation + GobInvalidate — which
   *  GameSession installs (and passes true). Every other embedder (the replay
   *  viewer's render-only sim host, future spectator surfaces) defaults to the
   *  SDL-era full-repaint-per-frame: correct without seams, and simply the
   *  previously-shipped behavior. */
  scopedInvalidation?: boolean;
  // --- build-time data (the art / display partition inputs) ---
  pakr: PackFileReader;
  tam: TexAtlasMgr;
  coords: CoordMap;
  /** game.ini — the [Fonts] table (gapfnt, game.cpp:419-431). */
  gameIni: IniReader;
  /** GobTemplates.ini sections (gpiniGobs) — the build UI's per-gt menu reads. */
  templates: IniSection[];
  /** The level's tile map name (level ini [General] TileMap, Level.cpp:207). */
  tmapName: string;
  /** The display size (gpdisp mode), e.g. { cx: 1024, cy: 768 }. */
  screen: { cx: number; cy: number };
  /** The DibBitmap surface factory (back dib, tilemap surface, minimap dib).
   *  Default = the CPU reference surface (oracle path + node tests). */
  createSurface?: (cx: number, cy: number) => CpuDibBitmap;
  /** gfShowFPS. */
  fShowFPS?: boolean;
  /** HostGetCurrentKeyState (host.cpp:738-760) — InputUIForm's arrow-key scroll. */
  hostGetCurrentKeyState?: (keyBits: number) => number;

  /** The shared gob-draw context. GameSession builds it BEFORE the input slice
   *  (which the SimView reads through) and injects the SAME instance here, so
   *  the renderer's hilightSpriteFor/getUpdateCount seams land on the very gctx
   *  the SimView draws with. Absent (a pure render-only host) → the renderer
   *  builds its own from pakr/tam/coords. */
  gctx?: GobDrawContext;
  /** The playfield-size object the host's pre-built input slice already closes
   *  over (its GetPlayfieldSize). The renderer FILLS this object in place
   *  (cx/cy) and keeps mutating it across resize, so the slice's closure — which
   *  fires inside this ctor's SetViewPos pin — reads the live size. Absent → the
   *  renderer owns a fresh one. */
  sizPlayfield?: { cx: number; cy: number };
  /** The overlay sprite manager + the per-gob hilight-sprite map. The render
   *  host draws/prunes them (displayFrameComplete) AND the input slice's sprite
   *  seams (Create/DestroyHilight/SelectionSprite, which fire during this ctor's
   *  form load) write them — so GameSession shares the SAME instances it already
   *  closed those seams over. Absent → the renderer owns fresh ones. */
  sprm?: SpriteManager;
  hilightSprites?: Map<unknown, AnimSprite>;

  // --- collaborators (real in GameSession; stub in the viewer) ---
  /** The input slice AND the sim-state bridge (gsim/ggobm/gpplrLocal reach) the
   *  SimView reads — built by the host, injected here. */
  sui: SimUIFormInput;
  gtimm: TimerMgr;
  evm: EventMgr;
  sndm: SoundMgr;
  options: OptionsState;

  // --- seams the render closures call (lazy where forward-referenced) ---
  /** sim.sim.GetUpdateCount() — Builder.cpp:373 upgrade-effect flash phase. */
  getUpdateCount: () => number;
  /** sim.ctx.gobm — displayFrameComplete's left-gob sprite prune. */
  getGobm: () => { GetGob(id: number, fAlloc: boolean): unknown };
  /** sim.ctx.pvars.Get — the form host's '$'/pvar variable fills. */
  getPvar: (name: string) => string | null | undefined;
  /** sim.ctx.animDataFor — the move-target anir (anidMoveTarget). */
  getAnimData: (name: string) => AnimData;
  /** the scripted host clock (HostGetTickCount / the FlickScroller ms clock). */
  getTickCount: () => number;
  /** the live option state $difficulty reads (gwfHandicap). */
  getHandicap: () => number;
  /** the live gfMouseMode the $iphone sysvar reads (mouse ⇒ '0', touch ⇒ '1') —
   *  mirrors sim.ctx.fTouchControls so canvas-label {$iphone} matches the sim. */
  getMouseMode: () => boolean;
  /** RunUpdatesNow (the MP catch-up) — lazy; null/no-op in the viewer. */
  runUpdatesNow?: () => void;
  /** m_pchatter->ShowChat() — lazy; null/no-op in the viewer. */
  showChat?: () => void;
  /** DoInGameMenu(shellEnv) — lazy (shellEnv built later); null in the viewer. */
  inGameMenu?: () => void;
  /** ShowObjectives(shellEnv, ...) — lazy; null in the viewer. */
  showObjectives?: (done?: () => void) => void;
  /** The persistent in-game HUD (Phase 5, Form 5):
   *  the HUD BOTTOM BAR DOM overlay. Threaded straight into InputUIForm's deps below
   *  — when set the form hides its OWN credits/power/menu controls (so they
   *  don't double-render) and pushes the live readout state each frame.
   *  Absent (a headless harness / the render-only viewer) ⇒ no UI shown. */
  domHud?: {
    update(state: {
      credits: number;
      needCredits: number;
      powerDemand: number;
      powerSupply: number;
      demandMax: number;
      flashOn: boolean;
    }): void;
  };
  /** The in-game OVERLAY-TEXT-LABEL DOM seam (Phase 5).
   *  Threaded into the SimUIHudDeps below — when set the SimUIForm suppresses the canvas
   *  paint of its 4 text labels (objective / alert / countdown / FPS) + pushes the model
   *  each frame. Absent (a headless harness / the render-only viewer) ⇒ no UI
   *  shown. */
  domSimUI?: { update(m: SimUIModel): void };
  /** Form::DoModal's nested event loop (form.cpp:578-622) — the host's modal
   *  pump seam. GameSession dissolves it into its advanceTicks pump (pushes the
   *  form onto its modal stack); no forms.ini Form exists now so it is never
   *  raised, but the seam is retained for the modal-pump machinery. */
  runModalLoop?: (pfrm: ModalForm, onLoopExit: () => void) => void;
}

/** The render stack — see the header. Public readonly fields are what the host
 *  / oracles read; `back` is mutable (resize() reassigns it). */
export class GameRenderer {
  readonly coords: CoordMap;
  readonly gctx: GobDrawContext;
  readonly fonts: Font[];
  readonly mfrmm: MultiFormMgr;
  readonly gpfrmmSim: FormMgr;
  readonly gpfrmmInput: FormMgr;
  readonly pfrmSimUI: SimUIForm;
  readonly pfrmInputUI: InputUIForm;
  readonly tmap: TileMap;
  readonly fog: ClientFogDraw;
  readonly strtbl: StringTable;
  /** gpsprm (Display::GetSpriteManager) — the overlay sprite stack; DrawSprites
   *  runs in displayFrameComplete. */
  readonly sprm: SpriteManager;
  /** UnitGob::m_panispr per hilighted gob (Unit.cpp:641-656) — host-owned. */
  readonly hilightSprites: Map<unknown, AnimSprite>;
  /** The shared back dib (gpmfrmm's dib) — frame() exposes its rgba; resize()
   *  recreates it (Display::SetSize, display.cpp:354-366). */
  back: CpuDibBitmap;
  /** The playfield (sim strip) size — ggame.GetPlayfieldSize. */
  readonly sizPlayfield: { cx: number; cy: number };
  /** The input (console) strip height (menu button + 2*4 margin). */
  readonly cyInput: number;

  private readonly deps: GameRendererDeps;
  /** Renderer seam: the surface factory (deps.createSurface; CPU default). */
  private readonly newSurface: (cx: number, cy: number) => CpuDibBitmap;

  constructor(deps: GameRendererDeps) {
    this.deps = deps;
    const { pakr, tam, coords } = deps;
    this.coords = coords;
    // the sprite stack — shared with the host's input slice (its sprite seams
    // write these during the form load below); a render-only host gets fresh
    this.sprm = deps.sprm ?? new SpriteManager();
    this.hilightSprites = deps.hilightSprites ?? new Map<unknown, AnimSprite>();

    // --- the art / draw stack over the shared pack. The host shares the gctx
    // its input slice already draws through (so these seams land on it);
    // render-only hosts let the renderer build one.
    const gctx = deps.gctx ?? new GobDrawContext(pakr, tam, coords);
    this.gctx = gctx;
    // Unit.cpp:499-507 — the selection-layer hilight capture reaches m_panispr
    gctx.hilightSpriteFor = (punt) => this.hilightSprites.get(punt) ?? null;
    // Builder.cpp:373 — the upgrade-effect flash phase
    gctx.getUpdateCount = () => deps.getUpdateCount();

    // gapfnt (game.cpp:419-431 — the game.ini [Fonts] table)
    const fonts: Font[] = [];
    for (let ifnt = 0; ifnt < 6; ifnt++) {
      const r = deps.gameIni.GetPropertyValue('Fonts', String(ifnt), '%s');
      if (r.count !== 1) throw new Error(`GameRenderer: game.ini font ${ifnt} missing`);
      const pfnt = LoadFont(pakr, tam, r.values[0] as string);
      if (pfnt === null) throw new Error(`GameRenderer: font ${r.values[0]} failed to load`);
      fonts.push(pfnt);
    }
    this.fonts = fonts;

    // --- the display partition (game.cpp:1316-1392): one back dib, the input
    // (console) strip is the MENU button height + 2*4 margin, sim strip above
    const { cx: W, cy: H } = deps.screen;
    const ptbmMenuButton = tam.CreateTBitmap('menuup.png');
    if (ptbmMenuButton === null) throw new Error('GameRenderer: menuup.png missing');
    const cyInput = ptbmMenuButton.GetSize().cy + 4 * 2;
    this.cyInput = cyInput;
    const cySim = H - cyInput;
    // fill the host's shared playfield object in place (so its pre-built input
    // slice's GetPlayfieldSize closure reads this live size during SetViewPos
    // below); a render-only host gets a fresh one
    this.sizPlayfield = deps.sizPlayfield ?? { cx: 0, cy: 0 };
    this.sizPlayfield.cx = W;
    this.sizPlayfield.cy = cySim;

    this.newSurface = deps.createSurface ?? ((cx, cy) => new CpuDibBitmap(cx, cy));
    this.back = this.newSurface(W, H);
    const pbmSim = this.back.Suballoc(0, cySim);
    const pbmInput = this.back.Suballoc(cySim, cyInput);

    // the level tile map at the SIM playfield size (Level.cpp:188-214)
    const tmap = LoadTileMap(pakr, deps.tmapName, { cx: W, cy: cySim }, deps.createSurface);
    if (tmap === null) throw new Error(`GameRenderer: ${deps.tmapName} failed to load`);
    this.tmap = tmap;
    const fog = new ClientFogDraw(coords);
    if (!fog.InitArt(pakr, tam)) throw new Error('GameRenderer: fog art failed to load');
    this.fog = fog;

    const hudDeps: SimUIHudDeps = {
      sui: deps.sui,
      tmap,
      fog,
      gtimm: deps.gtimm,
      anidMoveTarget: deps.getAnimData('movetarget.anir'),
      HostGetTickCount: () => deps.getTickCount(),
      getInputUIForm: () => this.pfrmInputUI ?? null,
      createSurface: deps.createSurface,
      fShowFPS: deps.fShowFPS ?? false,
      setPenEventInterval: (ct) => deps.evm.SetPenEventInterval(ct),
      // SimUI.cpp:910-918 runUpdatesNowEvent → RunUpdatesNow (the MP catch-up)
      runUpdatesNow: () => {
        deps.runUpdatesNow?.();
      },
      // SimUI.cpp:1608-1616 kidcChat → m_pchatter->ShowChat()
      showChat: () => {
        deps.showChat?.();
      },
      // gfLassoSelection/gfMouseMode (game.cpp:51-52) — SetUIType's handler choice
      options: deps.options,
      // The in-game overlay text labels (objective / alert / countdown /
      // FPS) (Phase 5). When set, the SimUIForm suppresses their canvas paint + pushes the model
      // each frame. Absent (a headless harness / the render-only viewer) ⇒ no UI shown.
      domSimUI: deps.domSimUI,
    };
    const inputDeps: InputUIDeps = {
      sui: deps.sui,
      getPenHandler: () => this.pfrmSimUI?.GetPenHandler() ?? null,
      gtimm: deps.gtimm,
      // HostGetCurrentKeyState (host.cpp:738-760) — the arrow-key scroll poll
      hostGetCurrentKeyState: deps.hostGetCurrentKeyState,
      // game/InputUI.cpp:321-451 — the M4b in-game MENU (gameoptions.ts body)
      inGameMenu: () => {
        deps.inGameMenu?.();
      },
      // game/InputUI.cpp:258-264 — the credits-label tap opens the objectives form
      showObjectives: (done) => {
        deps.showObjectives?.(done);
      },
      // The persistent DOM bottom bar (Phase 5, Form 5) (when set, the
      // form hides its own credits/power/menu controls + pushes the live readouts
      // each frame). Absent (a headless harness / the render-only viewer) ⇒ no UI shown.
      domHud: deps.domHud,
    };

    // canvas-removal R3: the forms.ini new-by-type-name control factory
    // (Form::Init, form.cpp:63-128) is GONE — LoadForm was its only caller and
    // R1/R2 made that test-only, so the whole control set (ButtonControl etc.,
    // misccontrols.ts) + HelpControl + EcomTextControl + their InitClass bitmap
    // statics were dead and are deleted. The in-game UI is renderer-owned
    // PaintLayers (SimUIForm/InputUIForm) + the DOM HUD.
    // gpstrtbl (game.cpp:436-440 strings.bin) — the alert raises + $ranktitle
    const strtbl = new StringTable();
    if (!strtbl.Init(pakr)) throw new Error('GameRenderer: strings.bin missing');
    this.strtbl = strtbl;

    const host = makeFormMgrHost(coords, {
      createTBitmap: (name: string) => tam.CreateTBitmap(name),
      getFont: (ifnt: number) => fonts[ifnt],
      // gsndm.PlaySfx (form.cpp button taps / form show+hide)
      playSfx: (sfx) => deps.sndm.PlaySfx(sfx),
      // Game::GetVar (game.cpp:2675-2734) — '$' sysvars + the pvars dictionary
      getVar: (pszName: string): string => {
        if (pszName.startsWith('$')) {
          const name = pszName.toLowerCase(); // stricmp
          if (name === '$ranktitle') {
            return GetRankTitle(strtbl, (n) => deps.getPvar(n));
          }
          if (name === '$grayscale') return '0'; // gfGrayscale — web is color
          if (name === '$iphone') return deps.getMouseMode() ? '0' : '1'; // touch ⇒ '1'
          if (name === '$difficulty') {
            switch (
              deps.getHandicap() // gwfHandicap
            ) {
              case kfHcapEasy:
                return '1';
              case kfHcapNormal:
                return '2';
              case kfHcapHard:
                return '3';
            }
            return '0';
          }
          if (name === '$demo') return '0'; // gfDemo — web ships full
          return '?var?'; // bogus request
        }
        return deps.getPvar(pszName) ?? '?pvar?';
      },
      // the real gevm wiring (the makeFormMgrHost defaults are harness no-ops)
      setRedrawFlags: (wf) => deps.evm.SetRedrawFlags(wf),
      postEvent: (pevt, fCoalesce) => deps.evm.PostEvent(pevt, fCoalesce),
      // gpdisp->FrameStart — scoped invalidation, see frameStartInvalidate.
      displayFrameStart: () => this.frameStartInvalidate(),
      // gpdisp->FrameComplete (display.cpp:291-307): draw the overlay sprites
      // after the form managers; prune sprites whose gob left the gob manager
      displayFrameComplete: () => {
        const gobm = deps.getGobm();
        for (const [punt, spr] of this.hilightSprites) {
          const g = punt as { GetId(): number };
          if (gobm.GetGob(g.GetId(), false) !== punt) {
            spr.Dispose();
            this.hilightSprites.delete(punt);
          }
        }
        this.sprm.DrawSprites(this.back, this.back.GetSize());
      },
      // Form::DoModal's nested event loop (form.cpp:578-622) — the host wires
      // the modal pump (GameSession dissolves it into its advanceTicks pump).
      // Omitted entirely when absent (no forms.ini Form is ever modal now).
      ...(deps.runModalLoop !== undefined ? { runModalLoop: deps.runModalLoop } : {}),
    });

    // gpmfrmm + gpfrmmSim + gpfrmmInput (game.cpp:1276-1392)
    this.mfrmm = new MultiFormMgr(host);
    if (!this.mfrmm.Init(this.back, false)) throw new Error('GameRenderer: mfrmm Init');
    this.gpfrmmSim = new FormMgr(host);
    if (!this.gpfrmmSim.Init(pbmSim, false)) throw new Error('GameRenderer: sim FormMgr Init');
    this.gpfrmmSim.GetUpdateMap()!.mapSizeProvider = () => tmap.GetMapSize();
    const rc = new Rect();
    rc.Set(0, 0, W, cySim);
    this.mfrmm.AddFormMgr(this.gpfrmmSim, rc);
    this.gpfrmmInput = new FormMgr(host);
    if (!this.gpfrmmInput.Init(pbmInput, false))
      throw new Error('GameRenderer: input FormMgr Init');
    rc.Set(0, cySim, W, cySim + cyInput);
    this.mfrmm.AddFormMgr(this.gpfrmmInput, rc);

    // The C++ global reaches of Simulation::SetViewPos's scroll plumbing
    // (Simulation.cpp:1005-1019): gpfrmmSim->Scroll keeps the sim update map's
    // tile origin/sub-tile offset tracking the view — without it
    // OnUpdateMapInvalidate's gob overflow-marking (SimUI.cpp:1376-1391)
    // misaligns and static gobs stop redrawing. These are RENDER seams over the
    // sim FormMgr, so the renderer wires them onto the input slice's host seam
    // table (it reads them live) over its OWN gpfrmmSim — which makes the
    // SetViewPos pin just below (and the viewer's render-only slice) work.
    deps.sui.host.Scroll = (dx, dy) => {
      this.gpfrmmSim.Scroll(dx, dy);
      // the scroll blit shifts already-drawn overlay-sprite pixels with the content —
      // damage their footprints so the scoped-invalidation repaint heals the stale copy
      this.sprm.ScrollDamage(dx, dy, this.invalidateSimRect);
    };
    deps.sui.host.ResetScroll = () => {
      this.gpfrmmSim.GetUpdateMap()!.Reset();
    };

    // Simulation::LoadState's view pin (Simulation.cpp:344 SetViewPos(wx,wy,true)):
    // reset the scroll bookkeeping, then SCROLL from (0,0) to the loaded view so
    // the sim update map's map-tile coordinate space lands aligned — before any
    // form exists in gpfrmmSim (the scroll touches no forms, only the map + dib).
    deps.sui.SetViewPos(deps.sui.sim.m_wxView, deps.sui.sim.m_wyView, true);

    // --- the in-game RENDER LAYERS, in Game::StartSim order (game.cpp:2030-2066).
    // HTML/DOM UI shell plan, Phase 3 (forms.ini elimination): SimUIForm/InputUIForm
    // are NO LONGER forms.ini-loaded Forms — they are renderer-owned PaintLayers. The
    // renderer Init()s each (no forms.ini, no control tree) then AddLayer()s it into
    // its region FormMgr's single-form slot; the FormMgr's proven incremental engine
    // (UpdateMap merge/scroll-blt + the paint walk + pen-capture cooking) drives them
    // UNCHANGED. The playfield render + the in-game pen/keyboard input are thus host-
    // owned, not behind a forms.ini Form.
    const pfrmSimUI = new SimUIForm(hudDeps);
    if (!pfrmSimUI.Init(this.gpfrmmSim)) throw new Error('GameRenderer: SimUILayer Init failed');
    this.gpfrmmSim.AddLayer(pfrmSimUI);
    this.pfrmSimUI = pfrmSimUI;
    const pfrmInputUI = new InputUIForm(inputDeps);
    if (!pfrmInputUI.Init(this.gpfrmmInput))
      throw new Error('GameRenderer: InputUILayer Init failed');
    this.gpfrmmInput.AddLayer(pfrmInputUI);
    this.pfrmInputUI = pfrmInputUI;
    // HTML/DOM UI shell plan, Phase 2 (forms.ini elimination): the canvas minimap form
    // (LoadForm(kidfMiniMap)) is GONE — the radar is now the DOM <canvas> MiniMapView
    // (dom/ingame/minimap-view.ts), mounted by main.ts and fed via gameSession.
    // minimapModel(). The back buffer is the FULL playfield; the DOM radar floats over its
    // bottom-right corner.

    // Game::SizeAndPositionEverything (game.cpp:2084-2119)
    this.SizeAndPositionEverything();
  }

  // game/game.cpp:2082-2119 Game::SizeAndPositionEverything — tell the sprite manager the
  // clip rects. The canvas minimap form is gone (Phase 2), so the playfield is no longer
  // carved out for it: sprites clip to the WHOLE playfield (clip1), and clip2 is empty
  // (zero-area → no second draw). The DOM radar floats above the bottom-right corner.
  SizeAndPositionEverything(): void {
    this.sprm.SetClipRects(
      { left: 0, top: 0, right: this.sizPlayfield.cx, bottom: this.sizPlayfield.cy },
      { left: 0, top: 0, right: 0, bottom: 0 },
    );
  }

  // The display resize entry: game/sdl/host.cpp:57-71 HostPollPendingResize —
  // Display::SetSize recreates the back dib (display.cpp:354-366), then
  // Game::OnDisplayResize (game.cpp:1258-1269) re-suballocs the partitions onto
  // the new dib, gpmfrmm->OnDisplayResize() re-inits the update maps + walks
  // every form's OnDisplayResize, SizeAndPositionEverything re-anchors the
  // minimap, and Level::OnDisplayResize re-sizes the tile map's draw surface.
  // RedrawEverything collapses to a full invalidate — the next pump paints.
  resize(cx: number, cy: number): void {
    // Display::SetSize
    (this.back as { dispose?: () => void } | undefined)?.dispose?.(); // free the old GL surface (no GC wait)
    this.back = this.newSurface(cx, cy);

    // Game::InitMultiFormMgr (the resize arm)
    const cySim = cy - this.cyInput;
    this.sizPlayfield.cx = cx;
    this.sizPlayfield.cy = cySim;
    this.mfrmm.SetDib(this.back);
    const rc = new Rect();
    rc.Set(0, 0, cx, cySim);
    this.gpfrmmSim.SetDib(this.back.Suballoc(0, cySim));
    this.mfrmm.SetFormMgrRect(this.gpfrmmSim, rc);
    rc.Set(0, cySim, cx, cySim + this.cyInput);
    this.gpfrmmInput.SetDib(this.back.Suballoc(cySim, this.cyInput));
    this.mfrmm.SetFormMgrRect(this.gpfrmmInput, rc);

    // gpmfrmm->OnDisplayResize()
    this.mfrmm.OnDisplayResize();

    // Game::OnDisplayResize's minimap/clip-rect pass + Level::OnDisplayResize
    this.SizeAndPositionEverything();
    this.tmap.SetPlayfieldSize(this.sizPlayfield);

    // RedrawEverything
    this.mfrmm.InvalidateRect(null);
    this.deps.evm.SetRedrawFlags(kfRedrawDirty);
  }

  /** Free this renderer's GL surfaces (back buffer + tile-map draw surface) deterministically. The
   *  FinalizationRegistry frees them only on GC, which doesn't feel off-heap GL pressure — so a caller
   *  that builds many renderers (the replay viewer rebuilds one per backward seek, and zooms resize)
   *  piles up textures until the tab is killed for memory. Call when discarding a renderer. */
  dispose(): void {
    (this.back as { dispose?: () => void } | undefined)?.dispose?.();
    this.tmap.dispose();
  }

  /** The shared back dib's rgba — current as of the last DrawFrame. */
  frame(): Uint8Array {
    return this.back.rgba;
  }

  /** Run the HUD refresh once (sets the static s_fReadyForPaint) — the
   *  render-only path calls this instead of OnTimer. Idempotent. */
  prime(): void {
    this.pfrmSimUI.Update();
  }

  /** The validated paint trigger (the gamePaintEvent arm of FilterEvent):
   *  gcPaint++ (the FPS stat) then the frame draw. */
  paint(): void {
    this.pfrmSimUI.gcPaint++;
    this.mfrmm.DrawFrame(true);
  }

  private readonly invalidateSimRect = (rc: {
    left: number;
    top: number;
    right: number;
    bottom: number;
  }): void => {
    this.gpfrmmSim.GetUpdateMap()!.InvalidateRect(rc);
  };

  /** gpdisp->FrameStart. The SDL display forced a FULL repaint every frame
   *  (display.cpp:282-289) because ITS back buffer was a non-persistent GPU target
   *  and incremental redraw dropped map sprites + overlay forms. Neither reason holds
   *  here: the GL back surface persists across presents, the in-game overlays are DOM,
   *  and the overlay sprites damage their own footprints (SpriteManager.CollectDamage/
   *  ScrollDamage). So the PLAYFIELD rides its real accumulated damage — gob
   *  invalidation + scroll blit + the fog/galaxite/minimap tile seams (GameSession) +
   *  the GobInvalidate seam — instead of re-rasterizing every visible tile and gob per
   *  paint; that per-paint fill was THE fill-rate multiplier on phone GPUs (issue #11's
   *  residual). The INPUT strip keeps the repaint-every-frame arm (it also heals the
   *  sim strip's tile overdraw into it). ?fullpaint restores the SDL behavior for live
   *  A/B diagnosis. */
  private frameStartInvalidate(): void {
    if (gFullRepaintPerFrame || this.deps.scopedInvalidation !== true) {
      this.mfrmm.InvalidateRect(null);
      return;
    }
    this.gpfrmmInput.InvalidateRect(null);
    this.sprm.CollectDamage(this.invalidateSimRect);
  }
}

/** ?fullpaint — restore the SDL-era full-repaint-per-frame (diagnosis escape hatch). */
export let gFullRepaintPerFrame = false;
export function setFullRepaintPerFrame(v: boolean): void {
  gFullRepaintPerFrame = v;
}
